import React, { useState, useRef, useEffect } from 'react';
import { Input } from '../Input';
import { getTokenInfo } from '@/utils';
import { useProviderInstance } from '@/context/ProviderContext';
import { ClearButton } from '@/views/MarKetBot/MarketBotLeft/Header/style';
import { MainTokenConfig, zeroAddress } from '@/config';
import { chainId, router } from '@/store';
import { useSelector } from 'react-redux';
import { StockTokenConfig, styles } from './config';

// ==========================================
// 1. 类型定义 (Types)
// ==========================================
export interface Token {
    symbol: string;
    decimals: number;
    address: string;
}

export type TabType = 'featured' | 'stock';

interface TokenSelectorProps {
    token: Token;   // ✅ 外部传入的代币，用于控制默认选中值
    onSelect: (token: Token | null) => void;
}


const TokenSelector: React.FC<TokenSelectorProps> = ({ token, onSelect }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [activeTab, setActiveTab] = useState<TabType>('featured');
    const _router = useSelector(router);
    const _chainId = useSelector(chainId);

    // ✅ 初始值优先用 props.token
    const [selectedToken, setSelectedToken] = useState<Token | null>(token ?? null);

    const [searchResult, setSearchResult] = useState<Token | null>(null);
    const [isSearching, setIsSearching] = useState<boolean>(false);

    const provider = useProviderInstance();

    const wrapperRef = useRef<HTMLDivElement>(null);
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const onSelectRef = useRef(onSelect);
    useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);


    // ✅ 监听 props.token 变化，同步到 selectedToken（避免内部选择和外部更新循环）
    useEffect(() => {
        // 如果两者地址一致，说明是同一条数据，不需要同步
        if (token?.address === selectedToken?.address) return;

        if (token) {
            setSelectedToken(token);
        } else {
            // 父组件传了 null，清空内部状态
            setSelectedToken(null);
        }
    }, [token]);

    // 点击外部关闭
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 清理防抖定时器
    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
        };
    }, []);

    // 过滤逻辑
    const sourceTokens: Token[] =
        activeTab === 'stock'
            ? StockTokenConfig
            : MainTokenConfig[_chainId];

    const filteredTokens = sourceTokens.filter(item =>
        item.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // 从列表中选择（或从查询结果中选择）
    const handleSelect = (t: Token) => {
        const { symbol, decimals, address } = t;
        const cleanToken: Token = { symbol, decimals, address };

        setSelectedToken(cleanToken);
        setIsOpen(false);
        setSearchTerm('');
        setSearchResult(null);
        onSelectRef.current(cleanToken);
    };

    // 手动输入地址查询（带防抖）
    const _spenTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const address = e.target.value;
        setSearchTerm(address);

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }

        setSearchResult(null);
        setIsSearching(false);

        if (!address) return;

        debounceTimerRef.current = setTimeout(async () => {
            try {
                if (address.startsWith('0x')) {
                    setIsSearching(true);
                    const { symbol_, decimals_ }: any = await getTokenInfo(address, provider);

                    if (symbol_ && decimals_ !== undefined) {
                        const newToken: Token = {
                            symbol: symbol_,
                            decimals: decimals_,
                            address: MainTokenConfig[_chainId][0].address === address &&
                                _router.label.includes("V4") ? zeroAddress : address,
                        };
                        setSearchResult(newToken);
                    } else {
                        setSearchResult(null);
                    }
                }
            } catch (error) {
                console.error('获取代币信息失败:', error);
                setSearchResult(null);
            } finally {
                setIsSearching(false);
            }
        }, 500);
    };

    // 清空搜索
    const handleClear = () => {
        setSearchTerm('');
        setSearchResult(null);
        setIsSearching(false);
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
    };

    const getTabLabel = (tab: TabType): string => {
        switch (tab) {
            case 'featured': return '精选';
            case 'stock': return '股票代币';
            default: return '';
        }
    };
    // 判断是否需要显示股票代币 Tab
    const showStockTab = React.useMemo(() => {
        const label = _router?.label
        return label.includes('flap') || label.includes('four') || (_chainId === 56 && label.includes('pancakeSwap V2'));
    }, [_router]);

    // 如果当前选中的是 stock，但又不允许显示 stock 时，回退到 featured
    useEffect(() => {
        if (!showStockTab && activeTab === 'stock') {
            setActiveTab('featured');
        }
    }, [showStockTab, activeTab]);

    return (
        <>
            <style>{styles}</style>
            <div className="token-selector-wrapper" ref={wrapperRef}>
                {/* 触发器 */}
                <div className="token-trigger" onClick={() => setIsOpen(!isOpen)}>
                    <div className="trigger-content">
                        {selectedToken ? (
                            <span className="token-symbol">{selectedToken.symbol}</span>
                        ) : (
                            <span className="placeholder">请选择或输入支付代币</span>
                        )}
                    </div>
                    <span className={`arrow-icon ${isOpen ? 'open' : ''}`}>▼</span>
                </div>

                {/* 弹框 */}
                {isOpen && (
                    <div className="token-dropdown">
                        <div className="dropdown-search">
                            <Input
                                type="text"
                                placeholder="搜索代币名称或粘贴地址..."
                                value={searchTerm}
                                onChange={_spenTokenChange}
                                autoFocus
                            />
                            {searchTerm && (
                                <ClearButton
                                    style={{ right: 20 }}
                                    onClick={handleClear}
                                >
                                    <img src="/close.png" alt="" />
                                </ClearButton>
                            )}
                        </div>
                        {showStockTab && <div className="dropdown-tabs">
                            {(['featured', 'stock'] as TabType[]).map((tab) => (
                                <button
                                    key={tab}
                                    className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                    type="button"
                                >
                                    {getTabLabel(tab)}
                                </button>
                            ))}
                        </div>}


                        <div className="dropdown-list">
                            {/* 优先级 1：正在查询链上代币 */}
                            {isSearching && (
                                <div className="empty-state">正在查询链上代币...</div>
                            )}

                            {/* 优先级 2：查询到了链上代币结果 */}
                            {!isSearching && searchResult && (
                                <div
                                    className="token-item chain-result"
                                    onClick={() => handleSelect(searchResult)}
                                >
                                    <div className="token-info">
                                        <div className="token-icon-box">🔗</div>
                                        <div className="token-text">
                                            <div className="token-item-symbol">{searchResult.symbol}</div>
                                            <div className="token-item-address">
                                                {searchResult.address.slice(0, 6)}...{searchResult.address.slice(-4)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* 优先级 3：正常列表 */}
                            {!isSearching && !searchResult && (
                                filteredTokens.length > 0 ? (
                                    filteredTokens.map((item) => (
                                        <div
                                            key={item.address}
                                            className={`token-item ${selectedToken?.address === item.address ? 'selected' : ''}`}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <div className="token-info">
                                                {/* <div className="token-icon-box">{item.icon}</div> */}
                                                <div className="token-text">
                                                    <div className="token-item-symbol">{item.symbol}</div>
                                                </div>
                                            </div>
                                            {selectedToken?.address === item.address && (
                                                <span className="check-icon">✓</span>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="empty-state">未找到相关代币</div>
                                )
                            )}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default TokenSelector;



// ==========================================
// 3. CSS 样式（不变）
// ==========================================
